import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  listDestinations,
  listScheduleTypes,
  listClassSections,
} from '../api/schoolConfigApi';
import { listUsers } from '../api/adminUsersApi';
import { logError } from '../utils/errorHandling';
import { usePassPolicy } from './usePassPolicy';

// Powers SetupChecklist.js — a post-wizard nudge banner on AdminPanel.js
// pointing out config areas that are empty or under-filled. The
// SchoolSetupWizard gate only guarantees "at least one schedule type
// exists"; this goes further and flags destinations/schedule types with
// only one entry too (a single schedule type can't model an assembly/delay
// day, a single destination is every student's only option), and flags
// class sections/teachers/pass policy whenever they're untouched.
//
// Each check mirrors the exact call the real panel already makes on mount
// (DestinationsPanel/ScheduleConfigPanel/ClassSectionsPanel/
// UserManagementPanel/PassPolicyPanel) rather than lifting their state up —
// this is a best-effort heuristic banner, not a source of truth, so a few
// duplicate GETs on the admin dashboard's mount is an acceptable tradeoff
// for keeping every panel independent (see AdminPanel.js's own header
// comment on that pattern).
//
// Fails open: any individual check that errors is logged and treated as
// still-incomplete, same rationale as AdminPanel.js's checkSchoolSetupStatus
// falling back to "show the wizard" on error — a non-fatal nudge should
// err toward nudging, not toward silently hiding a real gap.

const countOrZero = <T extends { data: unknown[] }>(
  promise: Promise<T>,
  label: string
): Promise<number> =>
  promise
    .then((r) => r.data.length)
    .catch((error: unknown) => {
      logError(error, 'client_api', `useAdminSetupChecklist:${label}`);
      return 0;
    });

const PAGE_SIZE_TEACHER_SCAN = 100; // server max (apps/user-api/src/schemas/user.ts listUsersSchema)

// Super-admin path has no server-side schoolId scoping for this endpoint
// (unlike the non-super-admin branch below, which relies on the caller's
// own school being enforced server-side), so it must page through
// TEACHER-role users platform-wide and filter client-side by schoolId.
// GET /api/users is cursor-paginated (default limit 50, max 100 — see
// apps/user-api/src/schemas/user.ts), so a single unbounded call silently
// truncates at the first page. The 'teachers' checklist item only has a
// zeroLabel (no oneLabel — see ITEM_DEFS), so this only needs a 0/1
// signal: it stops paging as soon as one matching-school teacher is
// found instead of accumulating a full count across every page.
const countMatchingTeachers = async (
  schoolId: number | string
): Promise<number> => {
  let cursor: string | undefined;
  do {
    const r = await listUsers({
      role: 'TEACHER',
      limit: PAGE_SIZE_TEACHER_SCAN,
      cursor,
    });
    if (r.data.some((u) => Number(u.schoolId) === Number(schoolId))) {
      return 1;
    }
    cursor = r.nextCursor ?? undefined;
  } while (cursor);
  return 0;
};

/** Keys of the individual setup areas this checklist tracks. */
type ChecklistKey =
  | 'destinations'
  | 'scheduleTypes'
  | 'classSections'
  | 'teachers'
  | 'passPolicy';

/** A single checklist definition (static config for one setup area). */
interface ItemDef {
  key: ChecklistKey;
  targetSectionId: string;
  zeroLabel: string;
  oneLabel?: string;
}

/** A rendered checklist entry surfaced to the banner. */
export interface AdminSetupChecklistItem {
  key: ChecklistKey;
  targetSectionId: string;
  label: string;
}

/** Return shape of {@link useAdminSetupChecklist}. */
export interface UseAdminSetupChecklistResult {
  items: AdminSetupChecklistItem[];
  loading: boolean;
}

/** Fetched counts per numeric setup area (null until first fetch resolves). */
interface Counts {
  destinations: number | null;
  scheduleTypes: number | null;
  classSections: number | null;
  teachers: number | null;
}

const ITEM_DEFS: ItemDef[] = [
  {
    key: 'destinations',
    targetSectionId: 'destination-management',
    zeroLabel:
      'Add a destination — students need at least one to request a pass.',
    oneLabel:
      'Add another destination — most schools need more than one option.',
  },
  {
    key: 'scheduleTypes',
    targetSectionId: 'schedule-variants',
    zeroLabel: 'Add a schedule type to define your school day.',
    oneLabel:
      'Add another schedule type — e.g. for assemblies, delays, or half days.',
  },
  {
    key: 'classSections',
    targetSectionId: 'class-sections',
    zeroLabel: 'Add a class section so passes can be tied to a class.',
  },
  {
    key: 'teachers',
    targetSectionId: 'user-account-management',
    zeroLabel: 'Add a teacher account.',
  },
  {
    key: 'passPolicy',
    targetSectionId: 'pass-policy',
    zeroLabel: 'Set a pass policy, or confirm you want no limits.',
  },
];

export function useAdminSetupChecklist(
  schoolId: number | string | null
): UseAdminSetupChecklistResult {
  const { userRole } = useAuth();
  const isSuperAdmin = userRole === 'developer';

  const { policy, loading: policyLoading } = usePassPolicy(schoolId);

  const [counts, setCounts] = useState<Counts>({
    destinations: null,
    scheduleTypes: null,
    classSections: null,
    teachers: null,
  });
  const [countsLoading, setCountsLoading] = useState(true);

  // Guards against a stale response clobbering `counts` for the wrong
  // schoolId: if schoolId changes while a fetch is in flight, the effect
  // below flips `cancelled` on cleanup so an earlier request that resolves
  // after a later one is dropped instead of overwriting state. Mirrors the
  // cancelled-flag pattern in AdminPanel.js's checkSchoolSetupStatus/getSchool
  // effects.
  const fetchCounts = useCallback(
    async (isCancelled: () => boolean): Promise<void> => {
      if (!schoolId) {
        if (!isCancelled()) {
          setCounts({
            destinations: null,
            scheduleTypes: null,
            classSections: null,
            teachers: null,
          });
          setCountsLoading(false);
        }
        return;
      }

      if (!isCancelled()) setCountsLoading(true);

      const [destinations, scheduleTypes, classSections, teachers] =
        await Promise.all([
          countOrZero(listDestinations(schoolId), 'destinations'),
          countOrZero(listScheduleTypes(schoolId), 'scheduleTypes'),
          countOrZero(listClassSections(schoolId), 'classSections'),
          (isSuperAdmin
            ? countMatchingTeachers(schoolId)
            : listUsers({ role: 'TEACHER', limit: 1 }).then((r) => r.data.length)
          ).catch((error: unknown) => {
            logError(error, 'client_api', 'useAdminSetupChecklist:teachers');
            return 0;
          }),
        ]);

      if (!isCancelled()) {
        setCounts({ destinations, scheduleTypes, classSections, teachers });
        setCountsLoading(false);
      }
    },
    [schoolId, isSuperAdmin]
  );

  useEffect(() => {
    let cancelled = false;
    fetchCounts(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [fetchCounts]);

  const loading = countsLoading || policyLoading;

  if (loading) {
    return { items: [], loading: true };
  }

  const values: Record<ChecklistKey, number | null> = {
    destinations: counts.destinations,
    scheduleTypes: counts.scheduleTypes,
    classSections: counts.classSections,
    teachers: counts.teachers,
    passPolicy: policy === null ? 0 : 1,
  };

  const items: AdminSetupChecklistItem[] = ITEM_DEFS.filter((def) => {
    const min = def.oneLabel ? 2 : 1;
    return Number(values[def.key]) < min;
  }).map((def) => ({
    key: def.key,
    targetSectionId: def.targetSectionId,
    label: values[def.key] === 0 ? def.zeroLabel : def.oneLabel ?? def.zeroLabel,
  }));

  return { items, loading: false };
}

export default useAdminSetupChecklist;
